"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Zap, Palette, Globe, Users, Star, Computer, Sparkles } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ModernTarotPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-violet-300">
                <Zap className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-violet-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-violet-500">/</span>
                <span className="text-sm">Modern Tarot Sistemleri</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/tarot/systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Modern Tarot Sistemleri</span>
            </div>

            <div className="bg-gradient-to-r from-violet-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-violet-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Zap className="h-10 w-10 text-violet-400" />
                Modern Tarot Sistemleri
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Çağdaş sanatçı ve yazarların yarattığı yeni yorumlar. Geleneksel 78 kart yapısını koruyarak
                modern temalar, çeşitli kültürler ve güncel konularla güncellenen sistemler.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Tarot'un Gelişimi</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Globe className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Küresel Yaklaşım</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    1970'lerden itibaren tarot dünya çapında yayılmaya başladı.
                    Farklı kültürler kendi perspektiflerini ekledi.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Çok-kültürlü sembolizm</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Bölgesel mitoloji entegrasyonu</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Indigenous wisdom traditions</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-700 rounded-lg flex items-center justify-center">
                      <Palette className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Sanatsal İnovasyon</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Geleneksel sembolik dili koruyarak modern sanat teknikleri
                    ve estetik yaklaşımları benimser.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Dijital art ve photography</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Mixed media yaklaşımlar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Contemporary illustration styles</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Tema Kategorileri</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  { name: "Urban/Şehir", icon: "🏙️", desc: "Modern şehir hayatı" },
                  { name: "Feminist", icon: "♀️", desc: "Kadın güçlenmesi" },
                  { name: "LGBTQ+", icon: "🏳️‍🌈", desc: "Çeşitlilik ve kapsayıcılık" },
                  { name: "Afrofüturizm", icon: "🚀", desc: "Afrikan diaspora" },
                  { name: "Cyberpunk", icon: "🤖", desc: "Teknoloji distopyası" },
                  { name: "Eco-Spiritual", icon: "🌱", desc: "Çevre bilinci" },
                  { name: "Minimalist", icon: "⚫", desc: "Sade tasarım" },
                  { name: "Pop Culture", icon: "🎬", desc: "Film, TV, müzik" },
                ].map((theme) => (
                  <div key={theme.name} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="text-2xl mb-2 text-center">{theme.icon}</div>
                    <div className="text-violet-400 font-semibold text-sm text-center">{theme.name}</div>
                    <div className="text-slate-400 text-xs text-center mt-1">{theme.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Öne Çıkan Modern Desteler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4">Indie & Artistik Desteler</h3>
                  <div className="space-y-3">
                    <div className="border-l-4 border-violet-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Modern Witch Tarot</h4>
                      <p className="text-sm text-slate-400">Lisa Sterle - Modern kadınlar RWS'ye uyarlandı</p>
                    </div>
                    <div className="border-l-4 border-blue-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Melanin Sun Tarot</h4>
                      <p className="text-sm text-slate-400">Tayannah Lee McQuillar - Afrocentric imagery</p>
                    </div>
                    <div className="border-l-4 border-green-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Next World Tarot</h4>
                      <p className="text-sm text-slate-400">Cristy Road - LGBTQ+ ve POC temsili</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4">Digital & Tech-Inspired</h3>
                  <div className="space-y-3">
                    <div className="border-l-4 border-cyan-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Cyberpunk Tarot</h4>
                      <p className="text-sm text-slate-400">Futuristik teknoloji temalı</p>
                    </div>
                    <div className="border-l-4 border-pink-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Pixel Tarot</h4>
                      <p className="text-sm text-slate-400">8-bit video game estetiği</p>
                    </div>
                    <div className="border-l-4 border-purple-400 pl-4">
                      <h4 className="font-semibold text-slate-200">Neon Moon Tarot</h4>
                      <p className="text-sm text-slate-400">80s retrofuture aesthetic</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Tarot Özellikleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">İnklusivite</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Çeşitli etnik kökenler, cinsel yönelimler,
                    yaş grupları ve beden türleri temsil edilir.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Computer className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Digital Integration</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    App'ler, augmented reality, dijital guidebook'lar
                    ve online community'ler.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Self-Care Odağı</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Mental health awareness, mindfulness,
                    personal empowerment ve healing.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaratma Süreci</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Konsept Geliştirme</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• <strong>Tema Seçimi:</strong> Hedef kitle ve mesaj belirleme</li>
                      <li>• <strong>Kültürel Araştırma:</strong> Respect ve authenticity</li>
                      <li>• <strong>Görsel Dil:</strong> Sanat stili ve color palette</li>
                      <li>• <strong>Sembolik Adaptasyon:</strong> Geleneksel anlamların güncellenmesi</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Üretim ve Dağıtım</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• <strong>Self-Publishing:</strong> Kickstarter, bağımsız yayın</li>
                      <li>• <strong>Print-on-Demand:</strong> Esnek üretim modeli</li>
                      <li>• <strong>Digital Versions:</strong> App'ler ve e-book formatlar</li>
                      <li>• <strong>Community Building:</strong> Social media ve fan engagement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Geleceğe Bakış</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Teknolojik Trendler</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">AI-assisted kart yaratımı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Virtual/Augmented Reality experiences</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Blockchain-based authenticity</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Sosyal Trendler</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Artan çeşitlilik ve temsiliyet</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Mental health ve wellness focus</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Collaborative ve community-driven creation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Modern tarot sistemleri yaratıcı ifade ve kişisel gelişim aracıdır ve kesinlik iddiası taşımaz.
                İçerikler tamamen merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için
                uzman görüşü alınmalıdır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/oracle"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Oracle Kartları
              </Link>

              <Link
                href="/about/tarot/systems"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Tarot Sistemleri
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}